import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Loader2, Send, Stethoscope } from "lucide-react";
import DoctorSelect from "../doctors/DoctorSelect";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatData(iso) {
  const raw = String(iso || "").slice(0, 10);
  const [year, month, day] = raw.split("-");
  if (!year || !month || !day) return "—";
  return `${day}/${month}/${year}`;
}

function dettaglioErrore(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return fallback;
}

function emailMedico(medico) {
  return String(medico?.email || medico?.email_blisteraggio || medico?.blistering_email || "").trim();
}

function etichettaStato(pratica) {
  if (pratica.scaduta) return { label: "Scaduta", className: "bg-red-100 text-red-800 hover:bg-red-100" };
  if (pratica.stato === "inviata") return { label: "Inviata", className: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" };
  if (pratica.stato === "completata") return { label: "Completata", className: "bg-slate-100 text-slate-700 hover:bg-slate-100" };
  if (pratica.stato === "annullata") return { label: "Annullata", className: "bg-slate-100 text-slate-500 hover:bg-slate-100" };
  if (pratica.in_scadenza) return { label: "In scadenza", className: "bg-amber-100 text-amber-900 hover:bg-amber-100" };
  return { label: "Da inviare", className: "bg-sky-100 text-sky-800 hover:bg-sky-100" };
}

export default function CambioMedicoSection({ fascicoloId, token, canEdit, onRefresh, onMedicoCuranteChange }) {
  const [pacchetto, setPacchetto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [nuovaAperta, setNuovaAperta] = useState(false);
  const [invioPratica, setInvioPratica] = useState(null);
  const [completaPratica, setCompletaPratica] = useState(null);
  const [annullaId, setAnnullaId] = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const base = `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/cambio-medico`;

  const carica = async () => {
    if (!fascicoloId || !token) return;
    setErrore("");
    try {
      const { data } = await axios.get(base, { headers });
      setPacchetto(data);
    } catch (error) {
      setErrore(dettaglioErrore(error, "Impossibile caricare le richieste di cambio medico"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fascicoloId, token]);

  const dopoMutazione = async () => {
    await carica();
    if (onRefresh) await onRefresh();
  };

  const scarica = async (pratica) => {
    setBusy(true);
    try {
      const response = await axios.get(`${base}/${pratica.id}/pdf`, {
        headers,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = pratica.pdf_file_name || "Modulo_cambio_medico.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(dettaglioErrore(error, "Impossibile scaricare il modulo"));
    } finally {
      setBusy(false);
    }
  };

  const annulla = async (pratica) => {
    setBusy(true);
    try {
      await axios.post(`${base}/${pratica.id}/annulla`, {}, { headers });
      toast.success("Richiesta annullata");
      setAnnullaId(null);
      await dopoMutazione();
    } catch (error) {
      toast.error(dettaglioErrore(error, "Impossibile annullare la richiesta"));
    } finally {
      setBusy(false);
    }
  };

  const pratiche = pacchetto?.pratiche || [];
  const prefill = pacchetto?.prefill;

  return (
    <Card className="mt-6 rounded-2xl border-slate-200 shadow-none hover:shadow-none" data-testid="cambio-medico-section">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Stethoscope className="w-5 h-5 text-teal-700" />
            Cambio e rinnovo medico
          </CardTitle>
          <p className="mt-1 text-sm text-slate-500">
            Il modulo si compila da solo con i dati dell&apos;ospite, del documento e del medico.
            Si invia al medico insieme alle copie dei documenti di identità.
          </p>
        </div>
        {canEdit && (
          <Button
            type="button"
            className="shrink-0 bg-teal-700 hover:bg-teal-800"
            onClick={() => setNuovaAperta(true)}
            disabled={!prefill}
            data-testid="btn-nuova-pratica-medico"
          >
            Nuova richiesta
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Caricamento richieste
          </div>
        ) : errore ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-red-700">{errore}</p>
            <Button type="button" variant="outline" size="sm" onClick={carica}>Riprova</Button>
          </div>
        ) : pratiche.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nessuna richiesta. La scadenza del riscontro resta tra gli avvisi finché la pratica è aperta.
          </p>
        ) : (
          <div className="space-y-3">
            {pratiche.map((pratica) => {
              const stato = etichettaStato(pratica);
              const tipo = pratica.tipo === "rinnovo" ? "Rinnovo" : "Cambio";
              const medico = pratica.medico_richiesto?.nome || "Medico da indicare";
              const aperta = pratica.stato === "pronta" || pratica.stato === "inviata";
              const giorni = pratica.giorni_rimanenti;
              const giorniTesto = giorni == null
                ? ""
                : giorni < 0
                  ? `scaduta da ${Math.abs(giorni)} ${Math.abs(giorni) === 1 ? "giorno" : "giorni"}`
                  : giorni === 0
                    ? "scade oggi"
                    : `tra ${giorni} ${giorni === 1 ? "giorno" : "giorni"}`;
              return (
                <div key={pratica.id} className="rounded-xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-slate-900">
                        {tipo} medico · {medico}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Richiesta {formatData(pratica.data_richiesta)}
                        {" · "}
                        Scadenza {formatData(pratica.data_scadenza)}
                        {giorniTesto && aperta ? ` · ${giorniTesto}` : ""}
                      </p>
                      {pratica.email_destinatario ? (
                        <p className="mt-0.5 text-xs text-slate-400">{pratica.email_destinatario}</p>
                      ) : null}
                    </div>
                    <Badge className={stato.className}>{stato.label}</Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => scarica(pratica)}>
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                      Scarica PDF
                    </Button>
                    {canEdit && aperta && (
                      <Button type="button" size="sm" disabled={busy} onClick={() => setInvioPratica(pratica)}>
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {pratica.stato === "inviata" ? "Reinvia al medico" : "Invia al medico"}
                      </Button>
                    )}
                    {canEdit && aperta && (
                      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setCompletaPratica(pratica)}>
                        Segna completata
                      </Button>
                    )}
                    {canEdit && aperta && annullaId !== pratica.id && (
                      <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setAnnullaId(pratica.id)}>
                        Annulla
                      </Button>
                    )}
                    {canEdit && annullaId === pratica.id && (
                      <>
                        <Button type="button" variant="destructive" size="sm" disabled={busy} onClick={() => annulla(pratica)}>
                          Conferma annullamento
                        </Button>
                        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setAnnullaId(null)}>
                          Indietro
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {prefill && (
        <NuovaRichiestaDialog
          open={nuovaAperta}
          onOpenChange={setNuovaAperta}
          prefill={prefill}
          token={token}
          busy={busy}
          onSubmit={async (payload) => {
            setBusy(true);
            try {
              const { data } = await axios.post(base, payload, { headers });
              if (data.invio?.ok) {
                toast.success(`Modulo inviato a ${data.invio.email}`);
              } else if (data.invio && data.invio.ok === false) {
                toast.error(data.invio.errore || "Modulo creato, invio non riuscito");
              } else {
                toast.success("Modulo generato");
              }
              setNuovaAperta(false);
              await dopoMutazione();
            } catch (error) {
              toast.error(dettaglioErrore(error, "Impossibile generare il modulo"));
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      <InvioDialog
        pratica={invioPratica}
        documenti={prefill?.documenti_identita || []}
        busy={busy}
        onClose={() => setInvioPratica(null)}
        onSubmit={async (payload) => {
          if (!invioPratica) return;
          setBusy(true);
          try {
            const { data } = await axios.post(`${base}/${invioPratica.id}/invia`, payload, { headers });
            toast.success(`Modulo inviato a ${data.email}`);
            setInvioPratica(null);
            await dopoMutazione();
          } catch (error) {
            toast.error(dettaglioErrore(error, "Invio non riuscito"));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CompletaDialog
        pratica={completaPratica}
        busy={busy}
        onClose={() => setCompletaPratica(null)}
        onSubmit={async (aggiorna) => {
          if (!completaPratica) return;
          setBusy(true);
          try {
            const { data } = await axios.post(
              `${base}/${completaPratica.id}/completa`,
              { aggiorna_medico_curante: aggiorna },
              { headers },
            );
            toast.success(data.medico_aggiornato
              ? "Richiesta completata e medico curante aggiornato"
              : "Richiesta segnata come completata");
            if (data.medico_aggiornato && onMedicoCuranteChange) {
              onMedicoCuranteChange(completaPratica.medico_richiesto || {});
            }
            setCompletaPratica(null);
            await dopoMutazione();
          } catch (error) {
            toast.error(dettaglioErrore(error, "Impossibile completare la richiesta"));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Card>
  );
}

function RiepilogoOspite({ ospite }) {
  const nome = `${ospite?.cognome || ""} ${ospite?.nome || ""}`.trim() || "Ospite";
  const documento = [ospite?.documento_tipo, ospite?.documento_numero].filter(Boolean).join(" ");
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Dati già nel fascicolo</p>
      <p className="mt-1 font-medium text-slate-900">{nome}</p>
      <p className="text-slate-600">
        CF {ospite?.codice_fiscale || "non indicato"}
        {" · "}
        nato il {formatData(ospite?.data_nascita)}
        {ospite?.luogo_nascita ? ` a ${ospite.luogo_nascita}` : ""}
      </p>
      <p className="text-slate-600">
        Documento {documento || "non indicato"}
        {ospite?.documento_scadenza ? ` · scade il ${formatData(ospite.documento_scadenza)}` : ""}
      </p>
      {!ospite?.codice_fiscale || !ospite?.documento_numero ? (
        <p className="mt-2 text-xs text-amber-800">
          Codice fiscale o estremi del documento mancano in anagrafica: il modulo li lascerà vuoti.
        </p>
      ) : null}
    </div>
  );
}

function SelezioneDocumenti({ documenti, selezionati, onToggle }) {
  if (!documenti.length) {
    return (
      <p className="text-sm text-amber-800">
        Nel fascicolo non ci sono copie di carta d&apos;identità o tessera sanitaria.
        Puoi generare il modulo lo stesso e allegarle dopo, oppure caricarle nel tab Anagrafica.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {documenti.map((doc) => (
        <div key={doc.file_id} className="flex items-start gap-2 text-sm text-slate-800">
          <Checkbox
            checked={selezionati.has(doc.file_id)}
            onCheckedChange={() => onToggle(doc.file_id)}
            className="mt-0.5"
          />
          <button type="button" className="text-left" onClick={() => onToggle(doc.file_id)}>
            <FileText className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
            {doc.file_name}
            {doc.descrizione ? <span className="text-slate-500"> — {doc.descrizione}</span> : null}
          </button>
        </div>
      ))}
    </div>
  );
}

function NuovaRichiestaDialog({ open, onOpenChange, prefill, token, busy, onSubmit }) {
  const [tipo, setTipo] = useState("cambio");
  const [medico, setMedico] = useState(null);
  const [dataRichiesta, setDataRichiesta] = useState(prefill.data_richiesta);
  const [dataScadenza, setDataScadenza] = useState(prefill.data_scadenza);
  const [note, setNote] = useState("");
  const [email, setEmail] = useState("");
  const [selezionati, setSelezionati] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    setTipo("cambio");
    setMedico(null);
    setDataRichiesta(prefill.data_richiesta);
    setDataScadenza(prefill.data_scadenza);
    setNote("");
    setEmail("");
    setSelezionati(new Set((prefill.documenti_identita || []).map((doc) => doc.file_id)));
  }, [open, prefill]);

  const attuale = prefill.medico_attuale || {};
  const emailDefault = tipo === "rinnovo" ? emailMedico(attuale) : emailMedico(medico);
  const emailEffettiva = email.trim() || emailDefault;
  const medicoPronto = tipo === "rinnovo" ? Boolean(attuale.nome) : Boolean(medico?.name);
  const dateOk = Boolean(dataRichiesta && dataScadenza && dataScadenza >= dataRichiesta);

  const inviaPayload = (invia) => {
    onSubmit({
      tipo,
      data_richiesta: dataRichiesta,
      data_scadenza: dataScadenza,
      note,
      medico_richiesto: tipo === "cambio" && medico
        ? {
            doctor_id: medico.id,
            nome: medico.name || "",
            telefono: medico.phone || "",
            email: medico.email || "",
            email_blisteraggio: medico.blistering_email || "",
            indirizzo: medico.address || "",
            asl: medico.asl_reference || "",
          }
        : null,
      documenti_identita_ids: [...selezionati],
      invia,
      email_destinatario: emailEffettiva || null,
    });
  };

  const toggle = (fileId) => {
    setSelezionati((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cambio-medico">
        <DialogHeader>
          <DialogTitle>Richiesta cambio o rinnovo medico</DialogTitle>
          <DialogDescription>
            {prefill.struttura?.nome
              ? `Il PDF esce intestato a ${prefill.struttura.nome}.`
              : "Il PDF usa i dati della struttura e del fascicolo."}
            {" "}La scadenza è la data entro cui chiedi il riscontro del medico.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className={`rounded-xl border px-3 py-2 text-left ${tipo === "cambio" ? "border-teal-700 bg-teal-50" : "border-slate-200"}`}
            onClick={() => setTipo("cambio")}
          >
            <span className="block text-sm font-medium">Cambio medico</span>
            <span className="block text-xs text-slate-500">Va al nuovo medico, con i documenti.</span>
          </button>
          <button
            type="button"
            className={`rounded-xl border px-3 py-2 text-left ${tipo === "rinnovo" ? "border-teal-700 bg-teal-50" : "border-slate-200"}`}
            onClick={() => setTipo("rinnovo")}
          >
            <span className="block text-sm font-medium">Rinnovo medico</span>
            <span className="block text-xs text-slate-500">Va al medico curante già in scheda.</span>
          </button>
        </div>

        <RiepilogoOspite ospite={prefill.ospite} />

        <div className="text-sm">
          <p className="text-xs text-slate-500">Medico attuale</p>
          <p className="font-medium text-slate-900">{attuale.nome || "Non indicato nel fascicolo"}</p>
          {emailMedico(attuale) ? <p className="text-xs text-slate-500">{emailMedico(attuale)}</p> : null}
        </div>

        {tipo === "cambio" ? (
          <div>
            <Label className="text-slate-500 text-xs">Medico richiesto</Label>
            <DoctorSelect
              token={token}
              value={medico?.id || ""}
              displayName={medico?.name || ""}
              onSelect={setMedico}
              onClear={() => setMedico(null)}
            />
          </div>
        ) : !attuale.nome ? (
          <p className="text-sm text-amber-800">Per il rinnovo indica prima il medico curante in questa scheda.</p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="cambio-data-richiesta" className="text-slate-500 text-xs">Data richiesta</Label>
            <Input
              id="cambio-data-richiesta"
              type="date"
              className="mt-1"
              value={dataRichiesta}
              onChange={(event) => setDataRichiesta(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="cambio-data-scadenza" className="text-slate-500 text-xs">Scadenza riscontro</Label>
            <Input
              id="cambio-data-scadenza"
              type="date"
              className="mt-1"
              value={dataScadenza}
              onChange={(event) => setDataScadenza(event.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500">Proposta a 30 giorni. Resta tra gli avvisi finché la richiesta è aperta.</p>
          </div>
        </div>
        {dataRichiesta && dataScadenza && dataScadenza < dataRichiesta ? (
          <p className="text-sm text-red-700">La scadenza non può precedere la data della richiesta.</p>
        ) : null}

        <div>
          <Label className="text-slate-500 text-xs">Documenti di identità da allegare</Label>
          <div className="mt-2">
            <SelezioneDocumenti
              documenti={prefill.documenti_identita || []}
              selezionati={selezionati}
              onToggle={toggle}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="cambio-email" className="text-slate-500 text-xs">Email del medico</Label>
          <Input
            id="cambio-email"
            type="email"
            className="mt-1"
            placeholder={emailDefault || "nome@studio.it"}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            {emailDefault
              ? `Se la lasci vuota usiamo ${emailDefault}.`
              : "Senza email puoi solo generare il PDF."}
          </p>
        </div>

        <div>
          <Label htmlFor="cambio-note" className="text-slate-500 text-xs">Note sul modulo</Label>
          <Textarea
            id="cambio-note"
            className="mt-1"
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Facoltative"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={busy || !medicoPronto || !dateOk}
            onClick={() => inviaPayload(false)}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Genera PDF"}
          </Button>
          <Button
            type="button"
            className="bg-teal-700 hover:bg-teal-800"
            disabled={busy || !medicoPronto || !dateOk || !emailEffettiva}
            onClick={() => inviaPayload(true)}
          >
            Genera e invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvioDialog({ pratica, documenti, busy, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [selezionati, setSelezionati] = useState(() => new Set());

  useEffect(() => {
    if (!pratica) return;
    setEmail(pratica.email_destinatario || emailMedico(pratica.medico_richiesto));
    const gia = new Set((pratica.documenti_identita || []).map((doc) => doc.file_id));
    setSelezionati(gia.size ? gia : new Set(documenti.map((doc) => doc.file_id)));
  }, [pratica, documenti]);

  return (
    <Dialog open={Boolean(pratica)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Invio al medico</DialogTitle>
          <DialogDescription>
            Il messaggio include il modulo e le copie selezionate. La scadenza sul PDF è il {formatData(pratica?.data_scadenza)}.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="invio-email-medico" className="text-slate-500 text-xs">Email</Label>
          <Input
            id="invio-email-medico"
            type="email"
            className="mt-1"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <SelezioneDocumenti
          documenti={documenti}
          selezionati={selezionati}
          onToggle={(fileId) => {
            setSelezionati((prev) => {
              const next = new Set(prev);
              if (next.has(fileId)) next.delete(fileId);
              else next.add(fileId);
              return next;
            });
          }}
        />
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Chiudi</Button>
          <Button
            type="button"
            className="bg-teal-700 hover:bg-teal-800"
            disabled={busy || !email.trim()}
            onClick={() => onSubmit({
              email_destinatario: email.trim(),
              documenti_identita_ids: [...selezionati],
            })}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompletaDialog({ pratica, busy, onClose, onSubmit }) {
  const [aggiorna, setAggiorna] = useState(true);
  useEffect(() => {
    if (pratica) setAggiorna(pratica.tipo === "cambio");
  }, [pratica]);
  const cambio = pratica?.tipo === "cambio";
  return (
    <Dialog open={Boolean(pratica)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Segna la richiesta come completata</DialogTitle>
          <DialogDescription>
            Esce dagli avvisi di scadenza. Il PDF resta nel fascicolo.
          </DialogDescription>
        </DialogHeader>
        {cambio ? (
          <div className="flex items-start gap-2 text-sm text-slate-800">
            <Checkbox checked={aggiorna} onCheckedChange={(value) => setAggiorna(value === true)} className="mt-0.5" />
            <button type="button" className="text-left" onClick={() => setAggiorna((value) => !value)}>
              Imposta {pratica?.medico_richiesto?.nome || "il medico richiesto"} come medico curante del fascicolo
            </button>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Chiudi</Button>
          <Button type="button" disabled={busy} onClick={() => onSubmit(cambio && aggiorna)}>
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
